import React, { useState, useRef, useEffect } from 'react';
import api from '@/services/api';
import { useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { X, PaperPlaneTilt, NavigationArrow } from '@phosphor-icons/react';

export default function HelpBot({ open, setOpen }) {
  const [messages, setMessages] = useState([
    { role: 'assistant', content: "Hey! I'm LotMate's help bot. Ask me anything about the platform — where to find things, how features work, or what a page does.", navigate_to: null },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);
  const inputRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const send = async () => {
    const msg = input.trim();
    if (!msg || loading) return;

    const userMsg = { role: 'user', content: msg };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const history = messages.filter(m => m.role !== 'system').slice(-10).map(m => ({ role: m.role, content: m.content }));
      const res = await api.post('/help-chat', {
        message: msg,
        current_page: location.pathname,
        history,
      });
      setMessages(prev => [...prev, { role: 'assistant', content: res.data?.reply || "I'm not sure — try rephrasing that.", navigate_to: res.data?.navigate_to || null }]);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: "Sorry, I couldn't process that. Try again.", navigate_to: null }]);
    }
    setLoading(false);
  };

  // Parse markdown-style links: **Go to [Page](/path)**
  const renderContent = (text) => {
    const parts = text.split(/(\*\*[^*]+\*\*|\[([^\]]+)\]\(([^)]+)\))/g);
    return parts.map((part, i) => {
      if (!part) return null;
      // Bold
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={i} className="font-bold">{part.slice(2, -2)}</strong>;
      }
      // Link
      const linkMatch = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      if (linkMatch) {
        return <a key={i} href={linkMatch[2]} className="text-[#c4a44a] underline hover:no-underline font-medium" onClick={(e) => { e.preventDefault(); window.location.href = linkMatch[2]; setOpen(false); }}>{linkMatch[1]}</a>;
      }
      return <span key={i}>{part}</span>;
    });
  };

  if (!open) return null;

  return (
    <div className="fixed top-14 left-2 right-2 sm:left-auto sm:right-4 sm:w-[360px] z-50 h-[520px] flex flex-col rounded-2xl overflow-hidden border border-border shadow-2xl bg-card">
      {/* Header */}
      <div className="flex items-center gap-2.5 px-4 py-3 bg-gradient-to-r from-[#1a1a1f] to-[#252530] shrink-0">
        <img src="/logo.svg" alt="" className="w-10 h-10 object-contain" style={{ margin: '-4px' }} />
        <div className="flex-1">
          <div className="text-xs font-bold text-white">LotMate Help</div>
          <div className="text-[9px] text-zinc-400">Ask anything about the platform</div>
        </div>
        <button onClick={() => setOpen(false)} className="w-7 h-7 rounded-lg flex items-center justify-center text-zinc-400 hover:text-white hover:bg-white/10 transition-colors">
          <X size={14} />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {messages.map((m, i) => (
          <div key={i} className={cn("flex", m.role === 'user' ? "justify-end" : "justify-start")}>
            <div className="max-w-[85%]">
              <div className={cn(
                "rounded-xl px-3 py-2 text-[12px] leading-relaxed",
                m.role === 'user'
                  ? "bg-[#c4a44a]/15 text-foreground rounded-br-sm"
                  : "bg-muted/50 text-foreground rounded-bl-sm border border-border/30"
              )}>
                {m.role === 'assistant' ? renderContent(m.content) : m.content}
              </div>
              {m.navigate_to && (
                <button
                  onClick={() => { navigate(m.navigate_to); setOpen(false); }}
                  className="mt-1.5 flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#c4a44a]/10 border border-[#c4a44a]/20 text-[#c4a44a] text-[10px] font-semibold hover:bg-[#c4a44a]/20 transition-colors w-full justify-center"
                >
                  <NavigationArrow size={11} weight="fill" /> Take me there
                </button>
              )}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-muted/50 border border-border/30 rounded-xl rounded-bl-sm px-3 py-2">
              <div className="flex gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/40 animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/40 animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/40 animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      {/* Quick suggestions */}
      {messages.length <= 1 && (
        <div className="px-3 pb-2 flex flex-wrap gap-1">
          {['Where do I change hours?', 'How do I add a team member?', 'What does AI Coach do?', 'How to create a campaign?'].map((q) => (
            <button key={q} onClick={() => { setInput(q); }} className="text-[9px] px-2 py-1 rounded-full border border-border/50 text-muted-foreground hover:text-foreground hover:bg-muted/30 transition-colors">
              {q}
            </button>
          ))}
        </div>
      )}

      {/* Input */}
      <div className="px-3 pb-3 pt-1 shrink-0">
        <div className="flex items-center gap-2 rounded-xl border bg-background px-3 py-1.5">
          <input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Ask me anything..."
            className="flex-1 text-xs bg-transparent outline-none placeholder:text-muted-foreground/50"
            disabled={loading}
          />
          <button
            onClick={send}
            disabled={!input.trim() || loading}
            className={cn(
              "w-7 h-7 rounded-lg flex items-center justify-center transition-colors shrink-0",
              input.trim() ? "bg-[#c4a44a] text-white hover:bg-[#b89a3e]" : "text-muted-foreground/30"
            )}
          >
            <PaperPlaneTilt size={13} weight="fill" />
          </button>
        </div>
      </div>
    </div>
  );
}
